import React, { useState, useEffect, useCallback } from 'react';
import { Screen } from '../types';

const PermitValidityScreen = ({ 
  validity,
  setValidity,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  visitingDistricts,
  setVisitingDistricts,
  checkGates,
  setCheckGates,
  navigateTo 
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [expandedSection, setExpandedSection] = useState('districts');

  const ALL_DISTRICTS = [
    'Tawang', 'West Kameng', 'East Kameng', 'Papum Pare',
    'Itanagar Capital Region', 'Lower Subansiri', 'Upper Subansiri', 'Kurung Kumey',
    'Kra Daadi', 'Pakke Kessang', 'Kamle', 'Lower Siang',
    'Lepa Rada', 'West Siang', 'Siang', 'East Siang',
    'Upper Siang', 'Shi Yomi', 'Dibang Valley', 'Lower Dibang Valley',
    'Lohit', 'Anjaw', 'Namsai', 'Changlang', 'Tirap', 'Longding'
  ];

  const ALL_CHECK_GATES = [
    { name: 'Bhalukpong', district: 'West Kameng' },
    { name: 'Holongi', district: 'Papum Pare' },
    { name: 'Banderdewa', district: 'Papum Pare' },
    { name: 'Naharlagun Railway Station', district: 'Papum Pare' },
    { name: 'Gumto Railway Station', district: 'Papum Pare' },
    { name: 'Balemu', district: 'East Kameng' },
    { name: 'Sejosa', district: 'Pakke Kessang' }
  ];

  const options = [
    { id: '10 Days', days: 10, price: '₹150', desc: 'Short tourist visits', tag: 'Instant', instant: true },
    { id: '1 Month', days: 30, price: '₹350', desc: 'Extended travel', tag: 'Standard' },
    { id: '6 Months', days: 180, price: '₹1,600', desc: 'Long-term stay', tag: 'Documents required' }
  ];

  // Calculate end date based on start date and validity
  const calculateEndDate = useCallback((start, validityType) => {
    if (!start) return null;
    const end = new Date(start);
    if (validityType === '10 Days') {
      end.setDate(end.getDate() + 10);
    } else if (validityType === '1 Month') {
      end.setMonth(end.getMonth() + 1);
    } else if (validityType === '6 Months') {
      end.setMonth(end.getMonth() + 6);
    }
    return end;
  }, []);

  // Auto-calculate end date when start date or validity changes
  useEffect(() => {
    if (startDate && validity) {
      const calculatedEnd = calculateEndDate(startDate, validity);
      if (calculatedEnd) {
        setEndDate(calculatedEnd);
      }
    }
  }, [startDate, validity, calculateEndDate, setEndDate]);

  const handleDurationSelect = (opt) => {
    setValidity(opt.id);
    // End date will be auto-calculated by the useEffect
  };

  const formatDate = (date) => date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  const formatShortDate = (date) => date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });

  const getDaysBetween = (start, end) => {
    if (!start || !end) return 0;
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
    return days;
  };

  const openCalendar = () => {
    setCurrentMonth(startDate ? new Date(startDate) : new Date());
    setShowCalendar(true);
  };

  const handleDateSelect = (date) => {
    setStartDate(date);
    setShowCalendar(false);
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isStartDate = (date) => startDate && date.toDateString() === startDate.toDateString();

  const toggleDistrict = (d) => {
    if (visitingDistricts.includes(d)) setVisitingDistricts(visitingDistricts.filter(x => x !== d));
    else setVisitingDistricts([...visitingDistricts, d]);
  };

  const toggleGate = (g) => {
    if (checkGates.includes(g)) setCheckGates(checkGates.filter(x => x !== g));
    else setCheckGates([...checkGates, g]);
  };

  const canProceed = startDate && endDate && visitingDistricts.length > 0 && checkGates.length > 0;

  return (
    <div className="ilp-screen" style={{ background: '#f8fafc' }}>
      <div className="ilp-header white-bg">
        <button className="ilp-back-btn" onClick={() => navigateTo(Screen.ApplicationType)}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h2>Plan Your Trip</h2>
        <div className="ilp-header-spacer"></div>
      </div>

      <div className="ilp-validity-content" style={{ gap: '20px', padding: '20px' }}>
        {/* Step Badge */}
        <div style={{ textAlign: 'center' }}>
          <span className="ilp-step-badge">Final Step</span>
        </div>

        {/* Section 1: Places to Visit */}
        <div style={{ 
          background: '#fff', 
          borderRadius: '20px', 
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}>
          <div style={{ 
            padding: '16px 20px', 
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '12px', 
              background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2563eb'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                Where are you visiting?
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                Select districts and entry gates
              </p>
            </div>
          </div>

          {/* Districts Section */}
          <div style={{ borderBottom: '1px solid #f1f5f9' }}>
            <button 
              onClick={() => setExpandedSection(expandedSection === 'districts' ? null : 'districts')}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                  Visiting Districts
                </span>
                {visitingDistricts.length > 0 && (
                  <span style={{
                    padding: '2px 8px',
                    background: '#dbeafe',
                    color: '#2563eb',
                    borderRadius: '100px',
                    fontSize: '11px',
                    fontWeight: 700
                  }}>
                    {visitingDistricts.length} selected
                  </span>
                )}
              </div>
              <svg 
                width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
                style={{ transform: expandedSection === 'districts' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {expandedSection === 'districts' && (
              <div style={{ padding: '0 16px 16px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: '12px', 
                  marginBottom: '12px' 
                }}>
                  <button 
                    onClick={() => setVisitingDistricts(ALL_DISTRICTS)}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#2563eb', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      cursor: 'pointer' 
                    }}
                  >
                    Select All
                  </button>
                  <button 
                    onClick={() => setVisitingDistricts([])}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#64748b', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      cursor: 'pointer' 
                    }}
                  >
                    Clear
                  </button>
                </div>
                <div style={{ 
                  display: 'flex', 
                  flexWrap: 'wrap', 
                  gap: '8px' 
                }}>
                  {ALL_DISTRICTS.map(d => (
                    <button
                      key={d}
                      onClick={() => toggleDistrict(d)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '100px',
                        border: visitingDistricts.includes(d) ? '2px solid #2563eb' : '1px solid #e2e8f0',
                        background: visitingDistricts.includes(d) ? '#eff6ff' : '#fff',
                        color: visitingDistricts.includes(d) ? '#2563eb' : '#475569',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s'
                      }}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Gates Section */}
          <div>
            <button 
              onClick={() => setExpandedSection(expandedSection === 'gates' ? null : 'gates')}
              style={{
                width: '100%',
                padding: '14px 20px',
                background: 'transparent',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                  Entry / Check Gates
                </span>
                {checkGates.length > 0 && (
                  <span style={{
                    padding: '2px 8px',
                    background: '#dcfce7',
                    color: '#16a34a',
                    borderRadius: '100px',
                    fontSize: '11px',
                    fontWeight: 700
                  }}>
                    {checkGates.length} selected
                  </span>
                )}
              </div>
              <svg 
                width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"
                style={{ transform: expandedSection === 'gates' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {expandedSection === 'gates' && (
              <div style={{ padding: '0 16px 16px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: '12px', 
                  marginBottom: '12px' 
                }}>
                  <button 
                    onClick={() => setCheckGates(ALL_CHECK_GATES.map(g => g.name))}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#2563eb', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      cursor: 'pointer' 
                    }}
                  >
                    Select All
                  </button>
                  <button 
                    onClick={() => setCheckGates([])}
                    style={{ 
                      background: 'none', 
                      border: 'none', 
                      color: '#64748b', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      cursor: 'pointer' 
                    }}
                  >
                    Clear
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {ALL_CHECK_GATES.map(g => (
                    <button
                      key={g.name}
                      onClick={() => toggleGate(g.name)}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        border: checkGates.includes(g.name) ? '2px solid #16a34a' : '1px solid #e2e8f0',
                        background: checkGates.includes(g.name) ? '#f0fdf4' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        textAlign: 'left'
                      }}
                    >
                      <div>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 600, 
                          color: checkGates.includes(g.name) ? '#166534' : '#0f172a' 
                        }}>
                          {g.name}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          {g.district}
                        </div>
                      </div>
                      <div style={{
                        width: '22px',
                        height: '22px',
                        borderRadius: '6px',
                        border: checkGates.includes(g.name) ? 'none' : '2px solid #d1d5db',
                        background: checkGates.includes(g.name) ? '#16a34a' : '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {checkGates.includes(g.name) && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Permit Duration & Entry Date */}
        <div style={{ 
          background: '#fff', 
          borderRadius: '20px', 
          padding: '20px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px', 
            marginBottom: '16px' 
          }}>
            <div style={{ 
              width: '40px', 
              height: '40px', 
              borderRadius: '12px', 
              background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                Permit Duration
              </h3>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>
                Select duration and entry date
              </p>
            </div>
          </div>

          {/* Duration Options */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
            {options.map((opt) => (
              <button
                key={opt.id}
                onClick={() => handleDurationSelect(opt)}
                style={{
                  padding: '14px 16px',
                  borderRadius: '14px',
                  border: validity === opt.id ? '2px solid #2563eb' : '1px solid #e2e8f0',
                  background: validity === opt.id ? '#eff6ff' : '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  textAlign: 'left'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: validity === opt.id ? 'none' : '2px solid #d1d5db',
                    background: validity === opt.id ? '#2563eb' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {validity === opt.id && (
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
                    )}
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '15px', 
                      fontWeight: 700, 
                      color: validity === opt.id ? '#1e40af' : '#0f172a' 
                    }}>
                      {opt.id}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>
                      {opt.desc}
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ 
                    fontSize: '17px', 
                    fontWeight: 800, 
                    color: validity === opt.id ? '#1e40af' : '#0f172a' 
                  }}>
                    {opt.price}
                  </div>
                  {opt.instant && (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      marginTop: '2px',
                      padding: '2px 6px',
                      background: '#dcfce7',
                      borderRadius: '4px',
                      fontSize: '9px',
                      fontWeight: 700,
                      color: '#16a34a',
                      textTransform: 'uppercase'
                    }}>
                      <svg width="10" height="10" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                      </svg>
                      {opt.tag}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>

          {/* Entry Date Selection */}
          <div style={{ 
            borderTop: '1px solid #f1f5f9', 
            paddingTop: '16px' 
          }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              marginBottom: '12px' 
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a' }}>
                Entry Date
              </span>
            </div>
            
            <button 
              onClick={openCalendar}
              style={{
                width: '100%',
                padding: '14px 16px',
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                background: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                transition: 'all 0.15s',
                textAlign: 'left'
              }}
            >
              <div style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#16a34a'
              }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M13.8 12H3" />
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '11px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>
                  When do you arrive?
                </div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: 700, 
                  color: startDate ? '#0f172a' : '#94a3b8',
                  marginTop: '2px'
                }}>
                  {startDate ? formatDate(startDate) : 'Select entry date'}
                </div>
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>

          {/* Trip Summary */}
          {startDate && endDate && (
            <div style={{
              marginTop: '16px',
              padding: '16px',
              background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              borderRadius: '14px',
              border: '1px solid #bbf7d0'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '12px'
              }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Trip Summary
                </span>
                <span style={{
                  padding: '4px 10px',
                  background: '#16a34a',
                  color: '#fff',
                  borderRadius: '100px',
                  fontSize: '11px',
                  fontWeight: 700
                }}>
                  {getDaysBetween(startDate, endDate)} Days
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#15803d', textTransform: 'uppercase' }}>Entry</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#166534' }}>{formatShortDate(startDate)}</div>
                </div>
                <div style={{ 
                  flex: 2, 
                  height: '2px', 
                  background: 'linear-gradient(90deg, #16a34a, #22c55e, #16a34a)',
                  borderRadius: '1px',
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '8px',
                    height: '8px',
                    background: '#16a34a',
                    borderRadius: '50%'
                  }} />
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: '10px', fontWeight: 600, color: '#15803d', textTransform: 'uppercase' }}>Exit</div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: '#166534' }}>{formatShortDate(endDate)}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        padding: '16px 20px 32px',
        background: '#fff',
        borderTop: '1px solid #f1f5f9'
      }}>
        {!canProceed && (
          <p style={{
            textAlign: 'center',
            fontSize: '12px',
            color: '#94a3b8',
            marginBottom: '12px'
          }}>
            {!visitingDistricts.length || !checkGates.length 
              ? 'Select at least one district and one gate' 
              : 'Select travel dates to continue'}
          </p>
        )}
        <button 
          className={`ilp-primary-btn full ${!canProceed ? 'disabled' : ''}`}
          disabled={!canProceed}
          onClick={() => navigateTo(Screen.Payment)}
        >
          <span>Proceed to Payment</span>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </button>
      </div>

      {/* Calendar Modal */}
      {showCalendar && (
        <div className="ilp-modal-overlay" onClick={() => setShowCalendar(false)}>
          <div className="ilp-calendar-modal" onClick={e => e.stopPropagation()}>
            <div className="ilp-calendar-header">
              <h3>Select Entry Date</h3>
              <button className="ilp-close-btn" onClick={() => setShowCalendar(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="ilp-calendar-hint" style={{ marginBottom: '16px' }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="#3b82f6">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>Exit date will be calculated based on {validity} duration</span>
            </div>

            <div className="ilp-calendar-nav">
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h4>{currentMonth.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</h4>
              <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            <div className="ilp-calendar-grid">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="ilp-calendar-day-name">{day}</div>
              ))}
              {generateCalendarDays().map((date, i) => (
                <div key={i} className="ilp-calendar-day-cell">
                  {date ? (
                    <button 
                      className={`ilp-calendar-day 
                        ${isStartDate(date) ? 'start-date' : ''} 
                        ${isDateDisabled(date) ? 'disabled' : ''}`}
                      onClick={() => !isDateDisabled(date) && handleDateSelect(date)}
                      disabled={isDateDisabled(date)}
                    >
                      {date.getDate()}
                    </button>
                  ) : <div className="ilp-calendar-empty"></div>}
                </div>
              ))}
            </div>

            <button className="ilp-secondary-btn full" onClick={() => setShowCalendar(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PermitValidityScreen;
